import React from 'react';

import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import { Input, InputGroup } from '@oysterjs/ui/input';
import { Spinner } from '@oysterjs/ui/spinner';

import { useLinkClientWorkspaceMutation, useSearchWorkspacesForClientQuery } from '~/api/graphql';
import { messageOfFailure } from '@oysterjs/core/graphql/errors';

import { FormError } from '~/components/form-error';
import { PickerRow } from '~/components/clients/picker-row';
import { useDebouncedValue } from '~/hooks/use-list-controls';
import { dateFormatter } from '~/components/table-formatters';

/** Enough to choose from without turning the dialog into a second list page. */
const PICKER_PAGE_SIZE = 10;

const LINK_FAILED = 'Could not link that workspace.';

/**
 * Picks a workspace to file under this client.
 *
 * Searches every workspace rather than only unassociated ones, because
 * searchCaraWorkspaces cannot filter on having no client. One already spoken for
 * names the client holding it and offers to move it here, which after the
 * backfill is most of them.
 */
export const LinkWorkspaceDialog = ({
  open,
  clientId,
  onClose,
  onLinked
}: {
  open: boolean;
  clientId: string;
  onClose: () => void;
  onLinked: () => void;
}) => {
  const [search, setSearch] = React.useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [error, setError] = React.useState<string | null>(null);
  const [moving, setMoving] = React.useState<string | null>(null);

  const [linkWorkspace, { loading: linking }] = useLinkClientWorkspaceMutation();

  const filter = React.useMemo(
    () => (debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
    [debouncedSearch]
  );

  const { data, loading } = useSearchWorkspacesForClientQuery({
    variables: { first: PICKER_PAGE_SIZE, filter },
    skip: !open
  });

  // On close, not on open: the debounce would still hold the last search, so
  // reopening would issue it and discard the result.
  React.useEffect(() => {
    if (!open) {
      setSearch('');
      setError(null);
      setMoving(null);
    }
  }, [open]);

  const workspaces = (data?.searchCaraWorkspaces.edges ?? []).map((edge) => edge.node);

  const link = async (workspaceId: string, replaceExisting = false) => {
    setError(null);
    const message = messageOfFailure(
      await linkWorkspace({ variables: { clientId, workspaceId, replaceExisting } }),
      LINK_FAILED
    );
    if (message) {
      setError(message);
      return;
    }
    onLinked();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} size="2xl">
      <DialogTitle>Link a workspace</DialogTitle>
      <DialogDescription>
        The workspace keeps everything it has. Its documents also start appearing on this client.
      </DialogDescription>
      <DialogBody>
        <InputGroup>
          <MagnifyingGlassIcon className="h-4 w-4" />
          <Input
            autoFocus
            type="search"
            placeholder="Search workspaces..."
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
        </InputGroup>

        <div className="mt-4 min-h-48">
          {loading && workspaces.length === 0 ? (
            <div className="py-12 text-center">
              <Spinner size="size-6" color="neutral" />
            </div>
          ) : workspaces.length === 0 ? (
            <p className="py-12 text-center text-sm text-neutral-500 dark:text-neutral-400">
              {search ? 'No workspaces match that search.' : 'No workspaces yet.'}
            </p>
          ) : (
            <ul className="divide-y divide-neutral-200 dark:divide-neutral-700">
              {workspaces.map((workspace) => {
                const heldBy = workspace.clients;
                const alreadyLinked = heldBy.some((held) => held.id === clientId);
                const heldByOthers = heldBy.length > 0 && !alreadyLinked;
                return (
                  <PickerRow
                    key={workspace.id}
                    name={workspace.title || 'Untitled workspace'}
                    detail={[
                      workspace.agencyUser?.name,
                      dateFormatter.format(new Date(workspace.updatedAt))
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    badge={
                      heldByOthers ? (
                        <Badge color="amber">On {heldBy.map((held) => held.name).join(', ')}</Badge>
                      ) : undefined
                    }
                    action={
                      alreadyLinked ? (
                        <Badge color="zinc">Already linked</Badge>
                      ) : heldByOthers ? (
                        // Confirmed first: the workspace leaves the other
                        // client's history, where someone may expect it.
                        <Button outline disabled={linking} onClick={() => setMoving(workspace.id)}>
                          Move here
                        </Button>
                      ) : (
                        <Button outline disabled={linking} onClick={() => link(workspace.id)}>
                          Link
                        </Button>
                      )
                    }
                  />
                );
              })}
            </ul>
          )}
        </div>

        <FormError message={error} className="mt-2 text-sm text-red-600 dark:text-red-400" />
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose}>
          Done
        </Button>
      </DialogActions>

      <MoveConfirmation
        workspace={workspaces.find((candidate) => candidate.id === moving)}
        onCancel={() => setMoving(null)}
        onConfirm={async (workspaceId) => {
          setMoving(null);
          await link(workspaceId, true);
        }}
      />
    </Dialog>
  );
};

/**
 * Moving is the only action here that takes something from another client, so it
 * is the only one that asks.
 */
const MoveConfirmation = ({
  workspace,
  onCancel,
  onConfirm
}: {
  workspace?: { id: string; title: string; clients: { id: string; name: string }[] };
  onCancel: () => void;
  onConfirm: (workspaceId: string) => void;
}) => (
  <Dialog open={!!workspace} onClose={onCancel} size="lg">
    <DialogTitle>Move this workspace?</DialogTitle>
    <DialogDescription>
      {workspace
        ? `"${workspace.title || 'Untitled workspace'}" will leave ${workspace.clients
            .map((held) => held.name)
            .join(', ')} and its documents will stop appearing there.`
        : ''}
    </DialogDescription>
    <DialogActions>
      <Button plain onClick={onCancel}>
        Cancel
      </Button>
      <Button color="sky" onClick={() => workspace && onConfirm(workspace.id)}>
        Move here
      </Button>
    </DialogActions>
  </Dialog>
);
